import { NetworkStatus, useQuery } from "@apollo/client";
import { useMemo } from "react";

import { CLOUD_INTEGRATION_CONFIG } from "./constants";
import { GET_ATTACHABLE_CLOUD_INTEGRATIONS_IN_SPACE } from "./gql";
import { CLOUD_INTEGRATIONS, GetAttachableCloudIntegrationsGql } from "./types";

const cloudConfig = [
  CLOUD_INTEGRATION_CONFIG.AWS,
  CLOUD_INTEGRATION_CONFIG.Azure,
  CLOUD_INTEGRATION_CONFIG.GCP,
];

const useAttachableCloudIntegrations = (spaceId: string) => {
  const { loading, data, refetch, networkStatus, error } =
    useQuery<GetAttachableCloudIntegrationsGql>(GET_ATTACHABLE_CLOUD_INTEGRATIONS_IN_SPACE, {
      variables: {
        spaceId,
      },
    });

  const attachableAwsIntegrations = useMemo(() => {
    return (data?.attachableAwsIntegrationsInSpace || [])
      .filter((integration) => !integration.legacy)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [data?.attachableAwsIntegrationsInSpace]);

  const attachableAzureIntegrations = useMemo(() => {
    return Array.from(data?.attachableAzureIntegrationsInSpace || []).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [data?.attachableAzureIntegrationsInSpace]);

  const cloudConfigFiltered = useMemo(
    () =>
      cloudConfig.filter(({ type }) => {
        if (type === CLOUD_INTEGRATIONS.AWS) {
          return attachableAwsIntegrations.length !== 0;
        }

        if (type === CLOUD_INTEGRATIONS.Azure) {
          return attachableAzureIntegrations.length !== 0;
        }
        return true;
      }),
    [attachableAwsIntegrations, attachableAzureIntegrations]
  );

  const hasData =
    !!data?.attachableAwsIntegrationsInSpace && !!data?.attachableAzureIntegrationsInSpace;

  return {
    cloudConfig: cloudConfigFiltered,
    loading,
    attachableAwsIntegrations,
    attachableAzureIntegrations,
    refetch,
    refetching: networkStatus === NetworkStatus.refetch,
    hasData,
    error,
  };
};

export default useAttachableCloudIntegrations;
